import { displayGpuHours } from "components/utils";

import type { BalanceStatusTone } from "../BalanceStatusIndicator";

export type ApplicationBalanceCurrencyDisplay = "credits" | "hours";

export interface ApplicationBalanceBreakdown {
  total: number;
  computeCost: number;
  storageCost: number;
}

export interface BuildApplicationBalanceModelOptions {
  currencyDisplay: ApplicationBalanceCurrencyDisplay;
  currentBalance: number;
  gpuHoursMostExpensive: number;
  gpuHoursLeastExpensive: number;
  mostExpensiveInstanceType?: string;
  leastExpensiveInstanceType?: string;
  burnBreakdown: ApplicationBalanceBreakdown;
  isDataLoading?: boolean;
  isWorkspaceLoading?: boolean;
  isGpuHoursLoading?: boolean;
  grantAccess?: {
    amount: string;
    headline: string;
    tone?: BalanceStatusTone;
  } | null;
}

export interface ApplicationBalanceModel {
  amount: string;
  currencyDisplay: ApplicationBalanceCurrencyDisplay;
  currentBalance: number;
  gpuHoursMostExpensive: number;
  gpuHoursLeastExpensive: number;
  mostExpensiveInstanceType?: string;
  leastExpensiveInstanceType?: string;
  burnBreakdown: ApplicationBalanceBreakdown;
  tone?: BalanceStatusTone;
  isDataLoading: boolean;
  isStatusLoading: boolean;
  isCallToAction: boolean;
  isDanger: boolean;
  showDetails: boolean;
  label?: string;
  grantHeadline?: string | null;
}

export const getBalanceStatusTone = (
  currentBalance: number,
  hourlyBurnRate: number,
): BalanceStatusTone => {
  if (hourlyBurnRate <= 0) return "healthy";

  const hoursRemaining = currentBalance / hourlyBurnRate;
  if (hoursRemaining > 24) return "healthy";
  if (hoursRemaining > 6) return "warning";
  return "critical";
};

export const formatGpuHoursRange = (
  mostExpensiveGpuHours: number,
  leastExpensiveGpuHours: number,
) => {
  if (mostExpensiveGpuHours < 0) return "<1 GPU hour";

  const lowerBound = displayGpuHours(mostExpensiveGpuHours);
  if (
    leastExpensiveGpuHours === 0 ||
    mostExpensiveGpuHours === leastExpensiveGpuHours
  ) {
    return `${lowerBound} GPU hours`;
  }

  return `${lowerBound}–${displayGpuHours(leastExpensiveGpuHours)} GPU hours`;
};

const formatCredits = (balance: number) =>
  balance.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

export const buildApplicationBalanceModel = ({
  currencyDisplay,
  currentBalance,
  gpuHoursMostExpensive,
  gpuHoursLeastExpensive,
  mostExpensiveInstanceType,
  leastExpensiveInstanceType,
  burnBreakdown,
  isDataLoading = false,
  isWorkspaceLoading = false,
  isGpuHoursLoading = false,
  grantAccess = null,
}: BuildApplicationBalanceModelOptions): ApplicationBalanceModel => {
  if (grantAccess && currencyDisplay === "hours") {
    return {
      amount: grantAccess.amount,
      label: "Days remaining",
      grantHeadline: grantAccess.headline,
      currencyDisplay,
      currentBalance,
      gpuHoursMostExpensive,
      gpuHoursLeastExpensive,
      mostExpensiveInstanceType,
      leastExpensiveInstanceType,
      burnBreakdown,
      tone: grantAccess.tone,
      isDataLoading,
      isStatusLoading: false,
      isCallToAction: false,
      isDanger: false,
      showDetails: !isDataLoading,
    };
  }

  const hasGpuRangeContext = Boolean(
    !isGpuHoursLoading &&
    mostExpensiveInstanceType &&
    leastExpensiveInstanceType,
  );
  const isCallToAction = currentBalance === 0;
  const isDanger = currentBalance < 0;
  const amount = isCallToAction
    ? "Add credits"
    : isDanger || currencyDisplay === "credits"
      ? formatCredits(currentBalance)
      : hasGpuRangeContext
        ? formatGpuHoursRange(gpuHoursMostExpensive, gpuHoursLeastExpensive)
        : "— GPU hrs";
  const hasGpuDetails = currencyDisplay === "hours" && hasGpuRangeContext;

  return {
    amount,
    currencyDisplay,
    currentBalance,
    gpuHoursMostExpensive,
    gpuHoursLeastExpensive,
    mostExpensiveInstanceType,
    leastExpensiveInstanceType,
    burnBreakdown,
    tone:
      !isDataLoading && burnBreakdown.total > 0
        ? getBalanceStatusTone(currentBalance, burnBreakdown.total)
        : undefined,
    isDataLoading,
    isStatusLoading:
      !isDataLoading && (isWorkspaceLoading || isGpuHoursLoading),
    isCallToAction,
    isDanger,
    showDetails:
      !isDataLoading &&
      !isWorkspaceLoading &&
      currentBalance > 0 &&
      (burnBreakdown.total > 0 || hasGpuDetails),
  };
};
